import clevertap from 'clevertap-web-sdk';

import config from '~/Config';

class CleverTapProvider {
  initialize = () => {
    try {
      clevertap.privacy.push({ optOut: false });
      clevertap.privacy.push({ useIP: false });
      clevertap.spa = true;
      // Required for Product Experiences (PE) Variables / A/B tests: variable
      // values are delivered over the personalization channel. Without this the
      // SDK sends the fetch but never processes the response or fires the
      // fetchVariables callback. Must be set before init().
      clevertap.enablePersonalization = true;
      clevertap.init(config.clevertap.accountId, config.clevertap.region);
    } catch (err) {
      console.error('CleverTap initialization failed:', err);
    }
  };

  identify = ({ id, email }) => {
    if (!id || !email) {
      return;
    }
    try {
      const identity = `${config.clevertap.prefix}${id}`;
      clevertap.onUserLogin.push({
        Site: {
          Identity: identity,
          Email: email,
        },
      });
      window?.AF?.('pba', 'setCustomerUserId', identity);
      // The user profile is now established, so the server can resolve this
      // user's PE Variables / A/B-test variant assignments. Pull them now.
      this.fetchVariables();
    } catch (err) {
      console.error('CleverTap identify failed:', err);
    }
  };

  // Pulls the current user's variable values (including A/B-test variant
  // assignments) from the CleverTap server. Must run after the profile is
  // established via onUserLogin, otherwise the server cannot resolve segments.
  fetchVariables = onSuccess => {
    try {
      clevertap.fetchVariables(() => {
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      });
    } catch (err) {
      console.error('CleverTap fetchVariables failed:', err);
    }
  };

  // Pushes locally-defined variable definitions up to the CleverTap dashboard so
  // the team can build experiments on them. This only succeeds from a test
  // profile, so it is gated to the staging environment and is a no-op in
  // beta/production.
  //
  // No automated call site by design: run this manually from the browser console
  // on staging after defining a new variable (`clevertapProvider.syncVariables()`)
  // to register it in the CleverTap dashboard so an experiment can be built on it.
  syncVariables = () => {
    if (config.clevertap.prefix !== 'staging_') {
      return;
    }
    try {
      clevertap.syncVariables();
    } catch (err) {
      console.error('CleverTap syncVariables failed:', err);
    }
  };

  logout = () => {
    try {
      clevertap.logout();
      window?.AF?.('pba', 'setCustomerUserId', '');
    } catch (err) {
      console.error('CleverTap logout failed:', err);
    }
  };

  logEvent = event => {
    event.logTo(clevertap);
  };

  recordTrackingConsent = consented => {
    clevertap.privacy.push({ optOut: !consented });
  };

  recordMarketingConsent = consented => {
    // CleverTap handles marketing preferences via profile properties
    if (consented !== undefined) {
      clevertap.profile.push({
        Site: { 'MSG-email': consented },
      });
    }
  };

  recordTrialVariant = variant => {
    // Called on the subscription screen's mount effect, just before Paddle
    // checkout is scheduled — guard so a throwing/ad-blocked SDK can't abort
    // the effect and block checkout. Matches the guarded network-path methods.
    try {
      clevertap.profile.push({ Site: { trialVariant: variant } });
    } catch (err) {
      console.error('CleverTap recordTrialVariant failed:', err);
    }
  };
}

export class CleverTapEvent {
  constructor(eventName, eventAttributes = {}) {
    this.eventName = eventName;
    this.eventAttributes = eventAttributes;
  }

  logTo = ct => {
    ct.event.push(this.eventName, this.eventAttributes);
    window?.AF?.('pba', 'event', {
      eventType: 'EVENT',
      eventName: this.eventName,
      eventValue: this.eventAttributes,
    });
  };
}

export class StartTrialPresentedEvent extends CleverTapEvent {
  constructor(platform) {
    super('Start Trial - Presented', { platform: platform });
  }
}

export class StartTrialEvent extends CleverTapEvent {
  constructor(productName, pricingGroup) {
    super('User - Start Trial', { productName: productName, pricingGroup: pricingGroup });
  }
}

export class UpgradeEvent extends CleverTapEvent {
  constructor(productName) {
    super('User - Upgrade', { productName: productName });
  }
}

export class DowngradeEvent extends CleverTapEvent {
  constructor(productName) {
    super('User - Downgrade', { productName: productName });
  }
}

export class ScreenViewEvent extends CleverTapEvent {
  constructor(screenName) {
    super(screenName);
  }

  logTo = ct => {
    ct.event.push(this.eventName);
    window?.AF?.('pba', 'event', {
      eventType: 'EVENT',
      eventName: `Screen View - ${this.eventName}`,
      eventValue: this.eventAttributes,
    });
  };
}

export class CreateEventEvent extends CleverTapEvent {
  constructor() {
    super('Events - Create');
  }
}

export class CreateHabitEvent extends CleverTapEvent {
  constructor() {
    super('Habits - Create');
  }
}

export class CreateHabitTrackedEvent extends CleverTapEvent {
  constructor() {
    super('HabitTracked - Create');
  }
}

export class CreateTaskEvent extends CleverTapEvent {
  constructor() {
    super('Tasks - Create');
  }
}

export class CompleteTaskEvent extends CleverTapEvent {
  constructor() {
    super('Tasks - Complete');
  }
}

export class RenameTaskSectionEvent extends CleverTapEvent {
  constructor(taskType) {
    super('Tasks - Rename Section', { taskType });
  }
}

export class MealPrepUpdateEvent extends CleverTapEvent {
  constructor() {
    super('Meal Preps - Update');
  }
}

export class RenameMealPrepSectionEvent extends CleverTapEvent {
  constructor() {
    super('Meal Preps - Rename Section');
  }
}

export class WaterTrackedEvent extends CleverTapEvent {
  constructor() {
    super('Water - Tracked');
  }
}

export class ListCreateEvent extends CleverTapEvent {
  constructor() {
    super('Lists - Create');
  }
}

export class FolderCreateEvent extends CleverTapEvent {
  constructor() {
    super('Folders - Create');
  }
}

export class SupportRequestEvent extends CleverTapEvent {
  constructor(category) {
    super('Support Request', { category });
  }
}

export class SelectCoverEvent extends CleverTapEvent {
  constructor(productId) {
    super('Theme - Select Cover', { productId: productId?.toString() });
  }
}

export class SelectFontEvent extends CleverTapEvent {
  constructor(name) {
    super('Theme - Select Font', { name });
  }
}

export class CalendarAccountExpandedEvent extends CleverTapEvent {
  constructor(provider) {
    super('Accounts - Expanded', { provider: provider });
  }
}

export class CalendarAccountConnectedEvent extends CleverTapEvent {
  constructor(provider) {
    super('Accounts - Connected', { provider: provider });
  }
}

export class CalendarAccountDisconnectedEvent extends CleverTapEvent {
  constructor(provider) {
    super('Accounts - Disconnected', { provider: provider });
  }
}

export class StartPlanningEvent extends CleverTapEvent {
  constructor() {
    super('Start Planning');
  }
}

export class ViewProductEvent extends CleverTapEvent {
  constructor(product) {
    super('Product View');
    this.product = product;
  }

  logTo = ct => {
    const { description, productable_id, productable_type, price } = this.product;
    const productAttributes = {
      Name: description,
      Id: `${productable_type}_${productable_id}`,
      Price: price,
      Quantity: 1,
    };
    ct.event.push(this.eventName, productAttributes);
    window?.AF?.('pba', 'event', {
      eventType: 'EVENT',
      eventName: 'Product - View Detail',
      eventValue: productAttributes,
    });
  };
}

export default new CleverTapProvider();
